import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEmail, MdArrowBack, MdCheckCircle } from "react-icons/md";
import AuthLayout from './AuthLayout';
import InputBox from '../common/InputBox';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

const { navigate } = useRestaurantNav();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSubmittedEmail(email);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
    }, 1500);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <AuthLayout
      title="Need help accessing"
      subtitle="Your Account?"
    >
      <div className="w-full max-w-md">
        <div className="backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-6 sm:p-8">
          {success ? (
            // Success View
            <>
              {/* Success Icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-orange-500/10 border-2 border-orange-500 flex items-center justify-center">
                  <MdCheckCircle className="w-12 h-12 text-orange-500" />
                </div>
              </div>

              {/* Success Message */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-3">
                  Check Your Email
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We've sent password reset instructions to<br />
                  <span className="text-orange-500 font-semibold">{submittedEmail}</span>
                </p>
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700 mb-6">
                <p className="text-gray-400 text-xs text-center leading-relaxed">
                  If you don't receive an email within a few minutes, please check your spam folder or try again.
                </p>
              </div>

              {/* Back to Login Button */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  style={{ borderRadius: '34px' }}
                  className="px-16 py-3.5 font-normal bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center gap-2 hover:from-orange-600 hover:to-orange-700 transition-colors"
                >
                  <MdArrowBack className="text-lg" />
                  <span>Back to Sign In</span>
                </button>
              </div>
            </>
          ) : (
            // Forgot Password Form View
            <>
              <h2 className="text-white text-center text-xl font-medium mb-2">
                Forgot Password?
              </h2>
              
              <p className="text-gray-500 text-[10px] text-center mb-6 leading-relaxed">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
              
              <div className="w-full h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent mb-8"></div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Input */}
                <div>
                  <label className="block text-gray-300 font-light text-xs mb-3">
                    Email
                  </label>
                  <InputBox
                    type="email"
                    id="email"
                    placeholder="name@restaurant.com"
                    onChange={(value) => setEmail(value)}
                    value={email}
                    required={true}
                    className="w-full bg-transparent border border-gray-700 px-4 py-2.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                    style={{ borderRadius: '34px' }}
                    icon={<MdEmail size={20} />}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    style={{ borderRadius: '34px' }}
                    className={`px-12 py-2.5 font-normal transition-colors duration-300 ${
                      loading 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                    }`}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>

                {/* Back to Login Link */}
                <div className="text-center pt-4">
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-4"></div>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-gray-400 hover:text-orange-500 transition-colors text-sm flex items-center justify-center gap-2 w-full"
                  >
                    <MdArrowBack className="text-base" />
                    <span>Back to Sign In</span>
                  </button>
                </div>
              </form>

              {/* Additional Help */}
              <div className="mt-6 p-4 rounded-xl bg-gray-800/50 border border-gray-700">
                <p className="text-gray-400 text-xs text-center leading-relaxed">
                  Still having trouble? Contact our support team at{' '}
                  <a 
                    href="mailto:support@promoflowai.com" 
                    className="text-orange-500 hover:text-orange-600 font-semibold transition-colors"
                  >
                    support@promoflowai.com
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default React.memo(ForgotPassword);